/**
 * Postfach-Mail bzw. Mail-Anhänge ablegen — mit sofort sichtbaren
 * Platzhaltern (User 01.11.2026, siehe `ablagePlatzhalter.ts`). Gemeinsam für
 * die Vorgangs-/To-do-Karten und die Kontakt-Dokumente.
 */
import { legeZugAb, type AblageErgebnis, type AblageZiel, type AblageZug } from "../../mail/postfachAblage";
import { mailAnhangName } from "../../vorgang/detail/todoDrop";
import type { NeuerPlatzhalter } from "./ablagePlatzhalter";
import type { AblagePlatzhalterApi } from "./useAblagePlatzhalter";

/** Wartezeit, wenn der Server meldet, dass seine Ablage noch läuft. */
export const NACHLADEN_WENN_LAEUFT_MS = 15_000;

export function platzhalterFuerZug(zug: AblageZug): NeuerPlatzhalter[] {
    if (zug.art === "postfachMail") {
        return [
            {
                name: mailAnhangName(zug.mail.subject),
                mimeType: "message/rfc822",
                text: zug.ohneAnhaenge ? "Mail wird abgelegt …" : "Mail wird samt Anhängen abgelegt …",
            },
        ];
    }
    return zug.anhaenge.anhaenge.map((a) => ({ name: a.name, mimeType: a.contentType ?? null, text: "Anhang wird abgelegt …" }));
}

type Api = Pick<AblagePlatzhalterApi, "starte" | "fertig" | "scheitern" | "laeuftWieder">;

/**
 * Legt ab und hält die Platzhalter auf Stand: geglückt → „fertig“ (der
 * Aufrufer räumt sie, sobald die echte Liste geladen ist), gescheitert → rot
 * mit „Erneut versuchen“ (derselbe Zug noch einmal — Anhang-Import und
 * Verknüpfung sind idempotent).
 */
export function legeZugAbSichtbar(
    zug: AblageZug,
    ziel: AblageZiel,
    api: Api,
    neuLaden: () => void | Promise<void>,
    warte: (f: () => void, ms: number) => void = (f, ms) => void setTimeout(f, ms),
): Promise<AblageErgebnis> {
    const eintraege = platzhalterFuerZug(zug);
    const ids = api.starte(eintraege);
    const nameVon = new Map(ids.map((id, i) => [id, eintraege[i].name]));

    const ausfuehren = async (aktiv: string[]): Promise<AblageErgebnis> => {
        const erg = await legeZugAb(zug, ziel);
        const gescheitert = new Set(erg.fehlgeschlagen);
        const ok: string[] = [];
        for (const id of aktiv) {
            const fehlt = zug.art === "postfachMail" ? erg.abgelegt === 0 : gescheitert.has(nameVon.get(id) ?? "");
            if (fehlt) {
                api.scheitern(id, erg.meldung, () => {
                    api.laeuftWieder(id);
                    void ausfuehren([id]);
                });
            } else {
                ok.push(id);
            }
        }
        if (ok.length > 0 && erg.laeuft) {
            // Der Server legt noch ab — Platzhalter stehen lassen und später
            // nachsehen, statt die Zeile ohne echten Eintrag zu räumen.
            warte(() => {
                api.fertig(ok);
                void neuLaden();
            }, NACHLADEN_WENN_LAEUFT_MS);
        } else if (ok.length > 0) {
            api.fertig(ok);
        }
        if (erg.abgelegt + erg.vorhanden > 0 && !erg.laeuft) await neuLaden();
        return erg;
    };
    return ausfuehren(ids);
}
